<template>
  <HrPageLayout
    title="Payroll Uploading"
    subtitle="Upload Payroll"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Payroll Reconciliation' },
      { label: 'Upload Payroll' }
    ]"
  >
    <div class="recon-upload-content">
      <div class="hr-chart-card">
        <div class="hr-chart-body hr-text-center">
          <div class="recon-upload-placeholder">
            <div class="recon-upload-icon">
              <i class="bx bx-cloud-upload" style="font-size: 64px; color: #059669;"></i>
            </div>
            <h4 class="hr-mt-3 hr-mb-2">Payroll Upload</h4>
            <p class="hr-text-muted">
              This section allows you to upload the monthly payroll Excel file for reconciliation.
              Ensure the file is formatted correctly before proceeding.
            </p>
            <p class="hr-fw-semibold hr-mb-4">Upload the payroll file to continue</p>
            <button type="button" class="hr-btn hr-btn-primary hr-btn-lg" @click="showPayrollModal = true">
              <i class="bx bx-upload"></i>
              Upload Payroll File
            </button>
          </div>
        </div>
      </div>

      <!-- Payroll Info Modal (Vue-controlled) -->
      <div v-if="showPayrollModal" class="hr-modal-overlay" @click.self="closePayrollModal">
        <div class="hr-modal-dialog">
          <div class="hr-modal-content">
            <div class="hr-modal-header">
              <h5 class="hr-modal-title">Payroll Information</h5>
              <button type="button" class="hr-modal-close" @click="closePayrollModal" aria-label="Close">
                <i class="bx bx-x"></i>
              </button>
            </div>

            <div v-if="!results" class="hr-modal-body">
              <div class="hr-form-grid" style="grid-template-columns: 1fr; gap: 1rem;">
                <div>
                  <label class="hr-form-label">Revenue Collected</label>
                  <div class="hr-input-group">
                    <span class="hr-input-addon">KSh</span>
                    <input type="number" v-model="revenueCollected" class="hr-input" placeholder="Enter amount" />
                  </div>
                </div>
                <div>
                  <label class="hr-form-label">Select Month</label>
                  <select v-model="payrollMonth" class="hr-select" required>
                    <option value="" disabled>Choose month...</option>
                    <option value="january">January</option>
                    <option value="february">February</option>
                    <option value="march">March</option>
                    <option value="april">April</option>
                    <option value="may">May</option>
                    <option value="june">June</option>
                    <option value="july">July</option>
                    <option value="august">August</option>
                    <option value="september">September</option>
                    <option value="october">October</option>
                    <option value="november">November</option>
                    <option value="december">December</option>
                  </select>
                </div>
                <div>
                  <label class="hr-form-label">Payroll File</label>
                  <input type="file" ref="payrollFileRef" class="hr-input" accept=".xlsx, .xls" />
                </div>
              </div>
              <div class="hr-mt-3">
                <button @click="uploadRecon" class="hr-btn hr-btn-primary hr-w-100">Submit</button>
              </div>
            </div>

            <div v-if="loading" class="hr-modal-body hr-text-center">
              <div class="hr-loading-wrap">
                <div class="hr-spinner"></div>
                <p>Processing payroll file...</p>
              </div>
            </div>

            <div v-if="results" class="hr-modal-body">
              <div class="hr-text-center">
                <i class="bx bx-check-circle hr-text-success" style="font-size: 48px;"></i>
                <h4 class="hr-mt-2 hr-mb-2">Payroll Upload Successful</h4>
                <p class="hr-text-muted hr-mb-4">
                  The payroll report has been uploaded successfully. Below is a summary of the key findings.
                </p>
                <div class="hr-alert hr-alert-warning hr-mb-2">
                  <strong>Discrepancies Detected</strong>
                  <p class="hr-mb-0">⚠️ 15 discrepancies were identified. Last Month: 10 discrepancies</p>
                </div>
                <div class="hr-alert hr-alert-info hr-mb-2">
                  <strong>Employee Count</strong>
                  <p class="hr-mb-0">👥 7,920 employees detected. Employee Register: 7,720 employees</p>
                </div>
                <div class="hr-alert hr-alert-primary hr-mb-4">
                  <strong>Wage Bill Comparison</strong>
                  <p class="hr-mb-0">💰 Payroll Wage Bill: Ksh 15,000,000. Employee Register: Ksh 14,500,000</p>
                </div>
                <button type="button" class="hr-btn hr-btn-primary hr-w-100" @click="gotTo('view_discrepancy'); closePayrollModal();">
                  <i class="bx bx-search-alt-2"></i> Review Detailed Discrepancies
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "ReconUploadPayroll",
  components: { HrPageLayout },
  data() {
    return {
      showPayrollModal: false,
      loading: false,
      results: false,
      revenueCollected: "",
      payrollMonth: ""
    };
  },
  methods: {
    closePayrollModal() {
      this.showPayrollModal = false;
      this.results = false;
      this.loading = false;
    },
    gotTo(route) {
      this.$router.push({ name: route });
    },
    async uploadRecon() {
      if (!this.payrollMonth) {
        this.$toast?.error?.("Please select a month") || alert("Please select a month");
        return;
      }
      const fileInput = this.$refs.payrollFileRef;
      if (!fileInput?.files?.length) {
        this.$toast?.error?.("Please select a payroll file") || alert("Please select a payroll file");
        return;
      }
      this.loading = true;
      this.results = false;
      try {
        const formData = new FormData();
        formData.append("function", "uploadPayrollRecon");
        formData.append("month", this.payrollMonth);
        formData.append("revenue", this.revenueCollected || "0");
        formData.append("file", fileInput.files[0]);
        const res = await execute(formData, authUrl);
        if (res?.data?.success) {
          this.results = true;
        } else {
          this.$toast?.error?.(res?.data?.message || "Upload failed") || alert(res?.data?.message || "Upload failed");
          this.results = false;
        }
      } catch (e) {
        this.$toast?.error?.(e?.message || "Upload failed") || alert(e?.message || "Upload failed");
        this.results = false;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.recon-upload-placeholder { padding: 2rem 1rem; }
.recon-upload-icon { margin-bottom: 0.5rem; }
.hr-mt-2 { margin-top: 0.5rem; }
.hr-mt-3 { margin-top: 1rem; }
.hr-mb-2 { margin-bottom: 0.5rem; }
.hr-mb-4 { margin-bottom: 1rem; }
.hr-fw-semibold { font-weight: 600; }
.hr-w-100 { width: 100%; }

.hr-input-group {
  display: flex;
  align-items: stretch;
}
.hr-input-addon {
  display: flex;
  align-items: center;
  padding: 0 12px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-right: none;
  border-radius: 10px 0 0 10px;
  font-weight: 600;
  color: #64748b;
}
.hr-input-group .hr-input { border-radius: 0 10px 10px 0; }

.hr-alert {
  padding: 12px 16px;
  border-radius: 10px;
  text-align: left;
}
.hr-alert-warning { background: #fef3c7; border: 1px solid #f59e0b; color: #92400e; }
.hr-alert-info { background: #dbeafe; border: 1px solid #3b82f6; color: #1e40af; }
.hr-alert-primary { background: #d1fae5; border: 1px solid #059669; color: #047857; }
</style>

